import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { concatMap, merge, take, timer, toArray } from 'rxjs';
import { useLoggedinUser, useService } from '@hooks';
import { RxUtils, Tr } from '@utils';
import { RoleWithAssignees, roleService, userService, userInvitationService, Permission } from '@services';
import { Button, Stack, Tab, Tabs } from '@mui/material';
import { EmptyEntityPageLayout, PortalPageTitle } from '@components/portal/common';
import { RoleAddUpdateDialog, RoleAssigneesDialog, RoleDeleteDialog, RoleDetailDialog, RoleList } from '@components/portal/collaborators';
import { ReactComponent as Illustration } from '@assets/illustrations/no-collaborators-added.svg';

const paddingX = 9;
type EntityType = RoleWithAssignees;
enum TabType {
	ALL = 'all',
	CUSTOM = 'custom',
	DEFAULT = 'default'
}

const filterEntities = (activeTab: TabType, roles?: RoleWithAssignees[]): RoleWithAssignees[] => {
	if (!Array.isArray(roles)) {
		return [];
	}

	if (activeTab === TabType.CUSTOM) {
		return roles.filter((role) => !role.isBuiltin);
	} else if (activeTab === TabType.DEFAULT) {
		return roles.filter((role) => role.isBuiltin);
	} else {
		return roles;
	}
};

export const ManageRoles: FC = () => {
	const user = useLoggedinUser();
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState<TabType>(TabType.ALL);
	const [addModal, setAddModal] = useState(false);
	const [roleAssignees, setRoleAssignees] = useState<RoleWithAssignees>();
	const [viewingEntity, setViewingEntity] = useState<RoleWithAssignees>();
	const [editingEntity, setEditingEntity] = useState<RoleWithAssignees>();
	const [deletingEntity, setDeletingEntity] = useState<RoleWithAssignees>();
	const { data: roles = [], trigger } = useService(() => roleService.getAllWithAssignees());
	const entities = filterEntities(activeTab, roles);
	const hasEntity = Array.isArray(entities) && entities.length;

	const onAdd = (role: Partial<EntityType>) =>
		RxUtils.promisify(
			roleService.create({ id: role.id, name: role.name, permissions: role.permissions, organizationId: role.organizationId }),
			trigger
		);
	const onEdit = (role: Partial<EntityType>) =>
		RxUtils.promisify(
			roleService.update({ id: role.id, name: role.name, permissions: role.permissions, organizationId: role.organizationId }),
			trigger
		);
	const onDelete = (role?: Partial<EntityType>) => {
		if (role) {
			RxUtils.promisify(
				merge(
					...(role?.users || []).map(({ email, roleId }) => userService.update({ email, roleId })),
					...(role?.invitedUsers || []).map(({ id, roleId }) => userInvitationService.update({ id, roleId }))
				).pipe(
					toArray(),
					concatMap(() => timer(1000).pipe(take(1))),
					concatMap(() => roleService.delete(role))
				),
				trigger,
				trigger
			);
		}
	};

	return (
		<Stack direction="column" spacing={3} sx={{ height: 1.0, maxHeight: 1.0, paddingY: 5 }}>
			<PortalPageTitle
				paddingX={3}
				title={<Tr.Portal path="roles.title" />}
				subtitle={<Tr.Portal path="roles.subtitle" />}
				onBackButtonClick={() => navigate(-1)}
			/>
			<Stack direction="column" flexGrow={1}>
				<Tabs value={activeTab} onChange={(_e, val) => setActiveTab(val)} centered>
					<Tab value={TabType.ALL} label={<Tr.Portal path="roles.tab-all" />} />
					<Tab value={TabType.CUSTOM} label={<Tr.Portal path="roles.tab-custom" />} />
					<Tab value={TabType.DEFAULT} label={<Tr.Portal path="roles.tab-default" />} />
				</Tabs>

				{hasEntity ? (
					<RoleList
						paddingX={paddingX}
						entities={entities}
						empty={<Illustration />}
						sx={{ marginTop: 4 }}
						onEdit={setEditingEntity}
						onView={setViewingEntity}
						onShowAssignees={setRoleAssignees}
						onDelete={setDeletingEntity}
					/>
				) : (
					<EmptyEntityPageLayout illustration={<Illustration />} title={<Tr.Portal path="roles.no-roles-added-title" />} />
				)}
			</Stack>
			{user?.hasPermission(Permission.ROLE_WRITE) && (
				<Stack direction="row" spacing={3} justifyContent="center" sx={{ paddingX }}>
					<Button
						data-testid="create-role-dialog-open-button"
						variant="contained"
						sx={{ textTransform: 'uppercase' }}
						onClick={() => setAddModal(true)}
					>
						<Tr.Portal path="roles.create-role" />
					</Button>
				</Stack>
			)}
			<RoleAssigneesDialog entity={roleAssignees} open={!!roleAssignees} onClose={() => setRoleAssignees(undefined)} />
			<RoleDetailDialog entity={viewingEntity} open={!!viewingEntity} onClose={() => setViewingEntity(undefined)} />
			<RoleAddUpdateDialog
				open={addModal}
				onClose={() => setAddModal(false)}
				onAccept={(entity) => (setAddModal(false), onAdd(entity))}
			/>
			<RoleAddUpdateDialog
				entity={editingEntity}
				open={!!editingEntity}
				onClose={() => setEditingEntity(undefined)}
				onAccept={(entity) => (setEditingEntity(undefined), onEdit(entity))}
			/>
			<RoleDeleteDialog
				roles={roles}
				entity={deletingEntity}
				open={!!deletingEntity}
				onClose={() => setDeletingEntity(undefined)}
				onAccept={(updatedEntity) => (setDeletingEntity(undefined), onDelete(updatedEntity))}
			/>
		</Stack>
	);
};
